import { differenceWith, get, isEqual, omit } from 'lodash';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import { FIELD_PATHS, REDUX_STORE } from '@funnels/constants';
import {
  Company,
  FetchEmploymentsAction,
  ReduxStoreData,
  StoreEmployment,
  UpdateV2EmploymentsAction,
  UpsertV2EmploymentsAction,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import {
  getEmploymentSortedByType,
  waitForAuth,
  waitForResourcesToBeCreated,
  waitForResourcesToBeFree,
} from '../_common';
import {
  fetchEmploymentsFail,
  fetchEmploymentsSuccess,
  updateV2Employments,
  updateV2EmploymentsFail,
  updateV2EmploymentsSuccess,
  upsertV2EmploymentsFail,
  upsertV2EmploymentsSuccess,
} from './actions';
import EmploymentsAPI from './api';
import { fixCompany, fixCompanyClientId, fixEmploymentId } from './utils';

export function* fetchEmploymentsWorker(action: FetchEmploymentsAction): any {
  try {
    yield waitForAuth();

    const { applicationId, applicantId } = action;
    let response;

    if (applicantId) {
      response = yield call(EmploymentsAPI.fetch, applicationId, applicantId);
    } else {
      response = yield call(EmploymentsAPI.fetchAll, applicationId);
    }

    const employments = response.data.map((employment: any) => omit(employment, ['meta', 'updatedAt']));
    yield put(fetchEmploymentsSuccess(getEmploymentSortedByType(employments)));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield put(fetchEmploymentsFail(e.response?.data || e.message));
  }
}

export function* updateV2EmploymentsWorker(action: UpdateV2EmploymentsAction): any {
  // applicantId only use for api purpose rest of time just use applicantClientId (applicantId!==applicantClientId only in create case)
  const { applicationId, employments: applicantEmployments, applicantClientId, applicantId } = action;
  try {
    yield waitForAuth();

    const requestApplicantEmployments = applicantEmployments.map((applicantEmployment: StoreEmployment) =>
      omit(applicantEmployment, ['applicantId', 'applicationId'])
    );

    const response = yield call(EmploymentsAPI.update, applicationId, applicantId, requestApplicantEmployments);

    // Because BE will omit companyClientId in response so need another fix for the companyClientId for employment
    const responseApplicantEmployments = (response.data || []).map((responseEmployment: StoreEmployment) => {
      fixCompanyClientId(responseEmployment, applicantEmployments);
      return responseEmployment;
    });

    yield put(updateV2EmploymentsSuccess(responseApplicantEmployments, applicantClientId));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield put(updateV2EmploymentsFail(e.response?.data || e.message));
  }
}

export function* upsertV2EmploymentsWorker(action: UpsertV2EmploymentsAction): any {
  try {
    yield waitForAuth();
    // applicantId only use for api purpose rest of time just use applicantClientId (applicantId!==applicantClientId only in create case)
    const { applicationId, employments: applicantEmployments, applicantClientId, applicantId } = action;

    let reduxStoreCompanies = yield select((state: ReduxStoreData) => state.companies.data);
    let reduxStoreEmployments: StoreEmployment[] = yield select((state: ReduxStoreData) => state.employments.data);

    yield waitForResourcesToBeCreated({
      companies: reduxStoreCompanies.map((c: Company) => c.clientId),
      employments: reduxStoreEmployments.map((e: StoreEmployment) => e.clientId),
    });
    yield waitForResourcesToBeFree(['companies']);
    reduxStoreCompanies = yield select((state: ReduxStoreData) => state.companies.data);
    reduxStoreEmployments = yield select((state: ReduxStoreData) => state.employments.data);

    const reduxStoreApplicantEmployments = reduxStoreEmployments.filter(
      (employment: StoreEmployment) =>
        get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(0)) === applicantClientId
    );

    // Filter out Id & companyId in the comparator function because its possible for that to be added by the store
    const changedEmployments = differenceWith(
      applicantEmployments,
      reduxStoreApplicantEmployments,
      ({ id: id1, companyId: companyId1, ...rest1 }, { id: id2, companyId: companyId2, ...rest2 }) =>
        isEqual(rest1, rest2)
    );

    // We rely on grabbing the id from the store so that
    // we DON'T rely on the store syncing to the form after initialisation
    if (changedEmployments.length > 0 || applicantEmployments.length !== reduxStoreApplicantEmployments.length) {
      const fixedEmployments = applicantEmployments.map((clientEmployment: StoreEmployment) => {
        // fix relationship between employments and company
        fixCompany(clientEmployment, reduxStoreCompanies);
        // fix BE id from redux store employment to form employment
        fixEmploymentId(clientEmployment, reduxStoreEmployments);
        return clientEmployment;
      });

      yield put(updateV2Employments(applicationId, fixedEmployments, applicantClientId, applicantId));
    }

    yield put(upsertV2EmploymentsSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    yield put(upsertV2EmploymentsFail(e.message));
  }
}

export function* employmentsWatcher() {
  yield takeEvery(REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.FETCH_EMPLOYMENTS, fetchEmploymentsWorker);
  yield takeEvery(REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPDATE_V2_EMPLOYMENTS, updateV2EmploymentsWorker);
  yield takeEvery(REDUX_STORE.EMPLOYMENTS.ACTION_TYPES.UPSERT_V2_EMPLOYMENTS, upsertV2EmploymentsWorker);
}

export default employmentsWatcher;
